import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { getGradesByClassroom } from '../../api/autenticoAPI';
import { getSubjects } from '../../api/sponteAPI';
import { logDebug } from '../../utils/logger';
import { normalizeSubjects } from '../../utils/sponteDataNormalizer';

const TalentoAutentico = ({ 
  open, 
  onClose, 
  studentsByClass, 
  classrooms, 
  period, 
  user 
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [gradesData, setGradesData] = useState({});
  const [error, setError] = useState(null);


  // Buscar disciplinas quando o modal abrir
  useEffect(() => {
    if (open && classrooms && classrooms.length > 0) {
      fetchSubjects();
    }
    // Reset data when modal closes
    if (!open) {
      setAllSubjects([]);
      setGradesData({});
      setSelectedSubject('');
      setCurrentTab(0);
      setError(null);
      setLoading(false);
    }
  }, [open, classrooms]);

  // Buscar todas as notas quando tiver disciplinas e período
  useEffect(() => {
    if (allSubjects.length > 0 && period && open) {
      fetchAllGrades();
    }
  }, [allSubjects, period, open]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!classrooms || !classrooms[1]) {
        throw new Error('Turma não encontrada. Selecione uma turma válida.');
      }
      
      logDebug('=== DEBUG TALENTO AUTÊNTICO ===');
      logDebug('Buscando disciplinas para turma:', classrooms[1]);
      logDebug('Nome da turma:', classrooms[0]);
      logDebug('Período selecionado:', period);
      logDebug('Usuário:', user);
      
      // Timeout de segurança
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout ao buscar disciplinas')), 15000)
      );
      
      const responsePromise = getSubjects(classrooms[1]);
      const response = await Promise.race([responsePromise, timeoutPromise]);
      
      logDebug('Resposta completa da API getSubjects:', response);
      
      if (!response) {
        throw new Error('API não retornou resposta. Verifique a conexão com o servidor.');
      }
      
      // Usar normalizador para garantir formato consistente
      let subjects = normalizeSubjects(response);
      
      logDebug('Disciplinas normalizadas:', subjects);
      
      // Normalizar os objetos das disciplinas para formato usado neste componente (id/name)
      const filteredSubjects = subjects.map(subject => ({
        id: subject.DisciplinaID || subject.id,
        name: subject.Nome || subject.name,
        // Manter campos originais para compatibilidade
        DisciplinaID: subject.DisciplinaID || subject.id,
        Nome: subject.Nome || subject.name
      }));
      
      // Se for professor, pode aplicar filtro adicional aqui no futuro
      // Por enquanto, mostra todas as disciplinas
      
      if (filteredSubjects.length === 0) {
        logDebug('Resposta da API:', response);
        logDebug('Disciplinas normalizadas:', subjects);
        logDebug('Disciplinas após filtro:', filteredSubjects);
        throw new Error(`Nenhuma disciplina válida encontrada para a turma ${classrooms[0]}. Verifique se a turma possui disciplinas cadastradas.`);
      }
      
      logDebug('Disciplinas carregadas:', filteredSubjects);
      setAllSubjects(filteredSubjects);
      setSelectedSubject(filteredSubjects[0].id);
      
    } catch (error) {
      console.error('Erro ao buscar disciplinas:', error);
      setError(error.message || 'Erro ao carregar disciplinas');
      setAllSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllGrades = async () => {
    try {
      setLoading(true);
      
      if (!allSubjects || allSubjects.length === 0) {
        logDebug('Nenhuma disciplina disponível para buscar notas');
        setGradesData({});
        return;
      }
      
      if (!classrooms || !classrooms[1] || !period) {
        console.error('Dados insuficientes para buscar notas:', { classrooms, period });
        return;
      }
      
      logDebug('Buscando notas para', allSubjects.length, 'disciplinas');
      
      const gradesPromises = allSubjects.map(async (subject) => {
        try {
          const result = await getGradesByClassroom(classrooms[1], subject.id, period);
          return { subjectId: subject.id, data: result };
        } catch (error) {
          console.error(`Erro ao buscar notas da disciplina ${subject.name}:`, error);
          return { subjectId: subject.id, data: { grades: [] } };
        }
      });
      
      const allGrades = await Promise.all(gradesPromises);
      const gradesMap = {};
      
      allGrades.forEach((gradeResult) => {
        const subject = allSubjects.find(s => s.id === gradeResult.subjectId);
        if (subject) {
          const grades = gradeResult.data?.grades || [];
          logDebug(`Disciplina ${subject.name} (ID: ${subject.id}):`, {
            totalGrades: grades.length,
            sampleGrade: grades[0] || 'Nenhuma nota encontrada',
            allGrades: grades
          });
          gradesMap[subject.id] = {
            subjectName: subject.name,
            grades: grades
          };
        }
      });
      
      logDebug('=== RESUMO NOTAS CARREGADAS ===');
      logDebug('Total de disciplinas com notas:', Object.keys(gradesMap).length);
      logDebug('Disciplinas:', Object.keys(gradesMap).map(id => ({
        id,
        name: gradesMap[id].subjectName,
        totalGrades: gradesMap[id].grades.length
      })));
      logDebug('Notas carregadas (detalhado):', gradesMap);
      logDebug('Alunos disponíveis:', studentsByClass?.length || 0);
      logDebug('Sample student:', studentsByClass?.[0]);
      setGradesData(gradesMap);
    } catch (error) {
      console.error('Erro ao buscar notas:', error);
      setGradesData({});
    } finally {
      setLoading(false);
    }
  };

  // Calcular ranking geral
  const generalRanking = useMemo(() => {
    if (!studentsByClass || Object.keys(gradesData).length === 0) {
      logDebug('=== RANKING GERAL: Dados insuficientes ===');
      logDebug('studentsByClass:', studentsByClass?.length || 0);
      logDebug('gradesData keys:', Object.keys(gradesData).length);
      return { rankings: [], hardestSubjects: [], subjectAverages: [] };
    }

    logDebug('=== CALCULANDO RANKING GERAL ===');
    logDebug('Total de alunos:', studentsByClass.length);
    logDebug('Total de disciplinas:', Object.keys(gradesData).length);

    // Primeiro, calcular a média de cada disciplina para identificar as mais difíceis
    const subjectAverages = {};
    Object.keys(gradesData).forEach(subjectId => {
      const subjectGrades = gradesData[subjectId].grades;
      let totalGrades = 0;
      let gradeCount = 0;
      
      logDebug(`Disciplina ${subjectId}: ${subjectGrades.length} notas`);
      
      subjectGrades.forEach((grade, index) => {
        // Log apenas as primeiras 3 notas para debug
        if (index < 3) {
          logDebug(`  Nota ${index + 1}:`, {
            AlunoID: grade.AlunoID,
            mb: grade.mb,
            rm: grade.rm,
            rb: grade.rb,
            fullGrade: grade
          });
        }
        
        const mbValue = parseFloat(String(grade.mb || '0').replace(',', '.')) || 0;
        const rmValue = parseFloat(String(grade.rm || '0').replace(',', '.')) || 0;
        const rbValue = parseFloat(String(grade.rb || '0').replace(',', '.')) || 0;
        const bestValue = Math.max(mbValue, rmValue, rbValue);
        
        if (bestValue > 0) {
          totalGrades += bestValue;
          gradeCount++;
        }
      });
      
      subjectAverages[subjectId] = gradeCount > 0 ? totalGrades / gradeCount : 0;
      logDebug(`  Média da disciplina ${subjectId}: ${subjectAverages[subjectId]}`);
    });

    // Identificar as 3 disciplinas mais difíceis (menores médias)
    const hardestSubjects = Object.entries(subjectAverages)
      .sort(([,a], [,b]) => a - b)
      .slice(0, 3)
      .map(([id]) => id);

    // Armazenar informações das disciplinas para exibição
    const hardestSubjectsInfo = Object.entries(subjectAverages)
      .sort(([,a], [,b]) => a - b)
      .slice(0, 3)
      .map(([id, avg]) => ({
        id,
        name: allSubjects.find(s => s.id === id)?.name || 'Disciplina não encontrada',
        average: Math.round(avg * 10) / 10
      }));

    const rankings = studentsByClass.map(student => {
      let totalScore = 0;
      let subjectCount = 0;
      let recoveryCount = 0;
      let hardSubjectsScore = 0;
      let hardSubjectsCount = 0;
      let allGrades = [];

      // Para cada disciplina, pegar a melhor nota (MB, RM ou RB)
      Object.keys(gradesData).forEach(subjectId => {
        const subjectGrades = gradesData[subjectId].grades;
        // Comparação flexível de IDs (pode ser string ou número)
        const studentGrade = subjectGrades.find(g => {
          const gradeAlunoID = String(g.AlunoID || '').trim();
          const studentAlunoID = String(student.AlunoID || '').trim();
          return gradeAlunoID === studentAlunoID || 
                 Number(gradeAlunoID) === Number(studentAlunoID);
        });
        
        if (studentGrade) {
          // Calcular a melhor nota após recuperação
          const mbValue = parseFloat(String(studentGrade.mb || '0').replace(',', '.')) || 0;
          const rmValue = parseFloat(String(studentGrade.rm || '0').replace(',', '.')) || 0;
          const rbValue = parseFloat(String(studentGrade.rb || '0').replace(',', '.')) || 0;
          
          let bestValue = mbValue;
          let hadRecovery = false;
          
          if (rmValue > bestValue) {
            bestValue = rmValue;
            hadRecovery = true;
          }
          if (rbValue > bestValue) {
            bestValue = rbValue;
            hadRecovery = true;
          }
          
          if (hadRecovery) recoveryCount++;
          
          totalScore += bestValue;
          subjectCount++;
          allGrades.push(bestValue);
          
          // Se é uma disciplina difícil, adicionar ao score especial
          if (hardestSubjects.includes(subjectId)) {
            hardSubjectsScore += bestValue;
            hardSubjectsCount++;
          }
        }
      });

      const average = subjectCount > 0 ? totalScore / subjectCount : 0;
      const hardSubjectsAverage = hardSubjectsCount > 0 ? hardSubjectsScore / hardSubjectsCount : 0;
      
      // Calcular desvio padrão (consistência)
      let standardDeviation = 0;
      if (allGrades.length > 1) {
        const mean = totalScore / subjectCount;
        const variance = allGrades.reduce((acc, grade) => acc + Math.pow(grade - mean, 2), 0) / allGrades.length;
        standardDeviation = Math.sqrt(variance);
      }
      
      return {
        student,
        average: Math.round(average * 10) / 10,
        recoveryCount,
        hardSubjectsAverage: Math.round(hardSubjectsAverage * 10) / 10,
        standardDeviation: Math.round(standardDeviation * 100) / 100,
        totalScore,
        subjectCount
      };
    });

    // Ordenar com critérios múltiplos de desempate
    const sortedRankings = rankings.sort((a, b) => {
      // 1. Média geral (principal)
      if (Math.abs(a.average - b.average) >= 0.01) {
        return b.average - a.average;
      }
      
      // 2. Número de recuperações (menos é melhor)
      if (a.recoveryCount !== b.recoveryCount) {
        return a.recoveryCount - b.recoveryCount;
      }
      
      // 3. Performance em disciplinas difíceis (maior é melhor)
      if (Math.abs(a.hardSubjectsAverage - b.hardSubjectsAverage) >= 0.01) {
        return b.hardSubjectsAverage - a.hardSubjectsAverage;
      }
      
      // 4. Consistência - menor desvio padrão é melhor
      if (Math.abs(a.standardDeviation - b.standardDeviation) >= 0.01) {
        return a.standardDeviation - b.standardDeviation;
      }
      
      // Sem outros critérios - mantém ordem original se todos os critérios forem iguais
      return 0;
    });

    // Adicionar posição considerando empates
    let currentPosition = 1;
    const rankingsWithPosition = sortedRankings.map((item, index) => {
      if (index > 0) {
        const prevItem = sortedRankings[index - 1];
        // Se qualquer critério é diferente, atualiza a posição
        if (Math.abs(item.average - prevItem.average) >= 0.01 || 
            item.recoveryCount !== prevItem.recoveryCount ||
            Math.abs(item.hardSubjectsAverage - prevItem.hardSubjectsAverage) >= 0.01 ||
            Math.abs(item.standardDeviation - prevItem.standardDeviation) >= 0.01) {
          currentPosition = index + 1;
        }
      }
      return {
        ...item,
        position: currentPosition
      };
    });

    return {
      rankings: rankingsWithPosition,
      hardestSubjects: hardestSubjectsInfo,
      subjectAverages: Object.entries(subjectAverages)
        .sort(([,a], [,b]) => a - b)
        .map(([id, avg]) => ({
          id,
          name: allSubjects.find(s => s.id === id)?.name || 'Disciplina não encontrada',
          average: Math.round(avg * 10) / 10
        }))
    };
  }, [studentsByClass, gradesData, allSubjects]);

  // Calcular ranking por disciplina
  const subjectSpecificRanking = useMemo(() => {
    if (!selectedSubject || !gradesData[selectedSubject] || !studentsByClass) return [];

    const subjectGrades = gradesData[selectedSubject].grades;
    
    const rankings = studentsByClass.map(student => {
      // Comparação flexível de IDs (pode ser string ou número)
      const studentGrade = subjectGrades.find(g => {
        const gradeAlunoID = String(g.AlunoID || '').trim();
        const studentAlunoID = String(student.AlunoID || '').trim();
        return gradeAlunoID === studentAlunoID || 
               Number(gradeAlunoID) === Number(studentAlunoID);
      });
      
      let finalGrade = 0;
      let hasRecovery = false;
      
      if (studentGrade) {
        const mbValue = parseFloat(String(studentGrade.mb || '0').replace(',', '.')) || 0;
        const rmValue = parseFloat(String(studentGrade.rm || '0').replace(',', '.')) || 0;
        const rbValue = parseFloat(String(studentGrade.rb || '0').replace(',', '.')) || 0;
        
        finalGrade = mbValue;
        if (rmValue > finalGrade) {
          finalGrade = rmValue;
          hasRecovery = true;
        }
        if (rbValue > finalGrade) {
          finalGrade = rbValue;
          hasRecovery = true;
        }
      }
      
      return {
        student,
        grade: Math.round(finalGrade * 10) / 10,
        hasRecovery
      };
    });

    const sortedRankings = rankings.sort((a, b) => {
      if (Math.abs(a.grade - b.grade) < 0.01) {
        return a.hasRecovery ? 1 : -1;
      }
      return b.grade - a.grade;
    });

    // Adicionar posição considerando empates
    let currentPosition = 1;
    return sortedRankings.map((item, index) => {
      if (index > 0) {
        const prevItem = sortedRankings[index - 1];
        // Se a nota e o status de recuperação são diferentes, atualiza a posição
        if (Math.abs(item.grade - prevItem.grade) >= 0.01 || 
            item.hasRecovery !== prevItem.hasRecovery) {
          currentPosition = index + 1;
        }
      }
      return {
        ...item,
        position: currentPosition
      };
    });
  }, [selectedSubject, gradesData, studentsByClass]);



  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getRankIcon = (position) => {
    if (position === 0) return <TrophyIcon sx={{ color: '#FFD700', fontSize: 28 }} />;
    if (position === 1) return <TrophyIcon sx={{ color: '#C0C0C0', fontSize: 24 }} />;
    if (position === 2) return <TrophyIcon sx={{ color: '#CD7F32', fontSize: 22 }} />;
    return <StarIcon sx={{ color: '#757575', fontSize: 20 }} />;
  };

  const getPerformanceColor = (average) => {
    if (average >= 9) return '#4CAF50';
    if (average >= 7) return '#2196F3';
    if (average >= 5) return '#FF9800';
    return '#F44336';
  };

  // Função auxiliar para obter a primeira letra do nome de forma segura
  const getInitialLetter = (name) => {
    if (!name) return '?';
    const nameStr = String(name).trim();
    return nameStr.length > 0 ? nameStr.charAt(0).toUpperCase() : '?';
  };

  // Função auxiliar para obter o nome formatado de forma segura
  const getStudentName = (name) => {
    if (!name) return 'Nome não disponível';
    return String(name);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Carregando dados do Talento Autêntico...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // Se há erro para mostrar
  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Talento Autêntico - Erro</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setError(null);
            if (classrooms && classrooms.length > 0) {
              fetchSubjects();
            }
          }}>
            Tentar Novamente
          </Button>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Se não há alunos na turma
  if (!studentsByClass || studentsByClass.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Talento Autêntico</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Nenhum aluno encontrado na turma selecionada.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Verificar se há notas disponíveis
  const hasGrades = generalRanking.rankings && generalRanking.rankings.length > 0 && 
                    generalRanking.rankings.some(r => r.subjectCount > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <AssessmentIcon sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" component="div">
            Talento Autêntico
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {classrooms && classrooms[0] ? `${classrooms[0]} - ` : ''}Análise de Desempenho - {period}º Bimestre
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<TrophyIcon />} 
            label="Ranking Geral" 
            iconPosition="start"
          />
          <Tab 
            icon={<SchoolIcon />} 
            label="Performance por Disciplina" 
            iconPosition="start"
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {currentTab === 0 && (
            // Tab de Ranking Geral
            <Grid container spacing={3}>
              {/* Tabela de Ranking */}
              <Grid item xs={12}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Ranking Geral da Turma
                    </Typography>
                    {!hasGrades && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Atenção:</strong> Nenhuma nota encontrada para o {period}º bimestre. 
                        Verifique se as notas foram cadastradas para esta turma e período.
                      </Alert>
                    )}
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                      <strong>Critérios de classificação:</strong> 1º Média Geral → 2º Menos Recuperações → 
                      3º Melhor performance em disciplinas mais difíceis → 4º Maior consistência (menor variação)
                    </Alert>
                    
                    {generalRanking.hardestSubjects && generalRanking.hardestSubjects.length > 0 && (
                      <Card sx={{ mb: 2, backgroundColor: '#fff3e0' }}>
                        <CardContent sx={{ py: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#f57c00' }}>
                            📊 Disciplinas Mais Difíceis da Turma (menor média geral):
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {generalRanking.hardestSubjects.map((subject, index) => (
                              <Chip
                                key={subject.id}
                                label={`${index + 1}º ${subject.name} (${subject.average})`}
                                color="warning"
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 'medium' }}
                              />
                            ))}
                          </Box>
                                                 </CardContent>
                       </Card>
                     )}

                    <Accordion sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          📈 Ranking Completo de Dificuldade das Disciplinas
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center"><strong>Posição</strong></TableCell>
                                <TableCell><strong>Disciplina</strong></TableCell>
                                <TableCell align="center"><strong>Média da Turma</strong></TableCell>
                                <TableCell align="center"><strong>Dificuldade</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {generalRanking.subjectAverages && generalRanking.subjectAverages.map((subject, index) => (
                                <TableRow key={subject.id}>
                                  <TableCell align="center">
                                    <Typography variant="body2">{index + 1}º</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {subject.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip 
                                      label={subject.average.toFixed(1)}
                                      size="small"
                                      sx={{ 
                                        backgroundColor: getPerformanceColor(subject.average),
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip 
                                      label={
                                        subject.average < 5 ? 'Muito Difícil' :
                                        subject.average < 6 ? 'Difícil' :
                                        subject.average < 7 ? 'Moderada' :
                                        subject.average < 8 ? 'Fácil' : 'Muito Fácil'
                                      }
                                      color={
                                        subject.average < 5 ? 'error' :
                                        subject.average < 6 ? 'warning' :
                                        subject.average < 7 ? 'primary' :
                                        subject.average < 8 ? 'info' : 'success'
                                      }
                                      variant="outlined"
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell align="center"><strong>Posição</strong></TableCell>
                            <TableCell><strong>Aluno</strong></TableCell>
                            <TableCell align="center"><strong>Média Geral</strong></TableCell>
                            <TableCell align="center">
                              <Tooltip title="Número de disciplinas em que precisou de recuperação">
                                <strong>Recuperações</strong>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Média nas 3 disciplinas mais difíceis da turma (menor média geral)">
                                <strong>Disciplinas Difíceis</strong>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Desvio padrão das notas (menor = mais consistente)">
                                <strong>Consistência</strong>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center"><strong>Performance</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {generalRanking.rankings.map((item, index) => (
                            <TableRow 
                              key={item.student.AlunoID}
                              sx={{ 
                                backgroundColor: item.position <= 3 ? '#f8f9fa' : 'inherit',
                                '&:hover': { backgroundColor: '#f0f0f0' }
                              }}
                            >
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  {getRankIcon(item.position - 1)}
                                  <Typography variant="h6">{item.position}º</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ 
                                    bgcolor: getPerformanceColor(item.average),
                                    width: 32, 
                                    height: 32 
                                  }}>
                                    {getInitialLetter(item.student.Nome)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                      {getStudentName(item.student.Nome)}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      Nº {item.student.NumeroChamada || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={item.average.toFixed(1)}
                                  sx={{ 
                                    backgroundColor: getPerformanceColor(item.average),
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '1rem'
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={item.recoveryCount}
                                  color={item.recoveryCount === 0 ? 'success' : 'warning'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={item.hardSubjectsAverage.toFixed(1)}
                                  sx={{ 
                                    backgroundColor: getPerformanceColor(item.hardSubjectsAverage),
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem'
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={item.standardDeviation.toFixed(2)}
                                  color={item.standardDeviation < 1.0 ? 'success' : 
                                         item.standardDeviation < 2.0 ? 'primary' : 'warning'}
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: getPerformanceColor(item.average),
                                    fontWeight: 'medium'
                                  }}
                                >
                                  {item.average >= 9 ? 'Excelente' :
                                   item.average >= 7 ? 'Bom' :
                                   item.average >= 5 ? 'Regular' : 'Insuficiente'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {currentTab === 1 && (
            // Tab de Performance por Disciplina
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={3}>
                  <CardContent>
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SchoolIcon />
                      <Typography variant="h6">
                        Performance por Disciplina
                      </Typography>
                      <FormControl sx={{ minWidth: 200, ml: 'auto' }}>
                        <InputLabel>Selecionar Disciplina</InputLabel>
                        <Select
                          value={selectedSubject}
                          onChange={(e) => setSelectedSubject(e.target.value)}
                          label="Selecionar Disciplina"
                        >
                          {allSubjects.map(subject => (
                            <MenuItem key={subject.id} value={subject.id}>
                              {subject.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {selectedSubject && (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell align="center"><strong>Posição</strong></TableCell>
                              <TableCell><strong>Aluno</strong></TableCell>
                              <TableCell align="center"><strong>Nota Final</strong></TableCell>
                              <TableCell align="center"><strong>Status</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {subjectSpecificRanking.map((item, index) => (
                              <TableRow 
                                key={item.student.AlunoID}
                                sx={{ 
                                  backgroundColor: item.position <= 3 ? '#f8f9fa' : 'inherit',
                                  '&:hover': { backgroundColor: '#f0f0f0' }
                                }}
                              >
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    {getRankIcon(item.position - 1)}
                                    <Typography variant="h6">{item.position}º</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ 
                                      bgcolor: getPerformanceColor(item.grade),
                                      width: 32, 
                                      height: 32 
                                    }}>
                                      {getInitialLetter(item.student.Nome)}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body1" fontWeight="medium">
                                        {getStudentName(item.student.Nome)}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        Nº {item.student.NumeroChamada || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={item.grade.toFixed(1)}
                                    sx={{ 
                                      backgroundColor: getPerformanceColor(item.grade),
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '1rem'
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={item.hasRecovery ? 'Recuperação' : 'Direto'}
                                    color={item.hasRecovery ? 'warning' : 'success'}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TalentoAutentico; 